// src/config/gridDensity.js
/**
 * Card geometry for the file grid — the one place these numbers exist.
 *
 * The grid renders two ways: a plain CSS grid up to VIRTUALIZE_THRESHOLD files,
 * and a windowed react-window grid above it. The windowed path has to know its
 * cell size in pixels, and those numbers used to be hand-copied into
 * FileGridView (`COLUMN_WIDTH = 155`, `ROW_HEIGHT = 207`) to mirror a 140px
 * minimum column, a 192px card and a 15px gap in FileBrowser.css.
 *
 * That made every card-size change a silent trap: the CSS moved, the JS didn't,
 * and the two only disagree above 150 files — so the misalignment showed up in
 * big workspaces and never in a developer's test data. Now the CSS reads these
 * as custom properties and the windowed path reads the same object.
 */

/**
 * `minWidth` is the narrowest a column may be (the grid fills the row with
 * `auto-fill`), `height` is the whole card, `thumb` is its preview area. A
 * density whose card is exactly as tall as its thumbnail has no room left for
 * the name — which is what makes the contact sheet a pure wall of previews.
 */
export const DENSITIES = {
    comfortable: { id: 'comfortable', label: 'Comfortable', minWidth: 140, height: 192, thumb: 116, gap: 15 },
    compact: { id: 'compact', label: 'Compact', minWidth: 112, height: 156, thumb: 96, gap: 12 },
    contact: { id: 'contact', label: 'Contact sheet', minWidth: 88, height: 88, thumb: 88, gap: 8 },
};

export const DEFAULT_DENSITY = 'comfortable';

export const isDensity = (id) => Object.prototype.hasOwnProperty.call(DENSITIES, id);

/** The geometry for an id, falling back to the default for anything unknown. */
export const densityOf = (id) => (isDensity(id) ? DENSITIES[id] : DENSITIES[DEFAULT_DENSITY]);

/** Windowed-grid cell metrics: the cell carries the gap, the card fills the rest. */
export const columnWidth = (density) => density.minWidth + density.gap;
export const rowHeight = (density) => density.height + density.gap;

/** The custom properties the grid's CSS reads. They inherit, so set them once. */
export const densityVars = (density) => ({
    '--qd-card-min-w': `${density.minWidth}px`,
    '--qd-card-h': `${density.height}px`,
    '--qd-card-thumb-h': `${density.thumb}px`,
    '--qd-card-gap': `${density.gap}px`,
});
